'use client';

import { CustomImage as Image } from '..';
import { useModal } from 'components/shared/Modal';
import { useTranslations } from 'next-intl';
import { PlusCircleDark } from 'assets/icons/PlusCircleDark';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import type { BvSubscriptionCard } from 'content/products/bloodVisionSubscriptionCards';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardFooterRow,
  ShopCardPlanTileMedia,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardBloodVisionSubscription.module.css';

interface ProductCardBloodVisionSubscriptionProps {
  className?: string;
  card: BvSubscriptionCard;
  isFirst?: boolean;
}

/**
 * Shop card for the Blood Vision Essential/Annual subscription plans.
 * These run on the Stripe/Flex rail, not Shopify, so instead of
 * add-to-cart the card opens the same upsell modal the one-time BV
 * products use (BloodVisionSubscriptionModal), where the whole purchase
 * completes: ZIP price check, test type, hosted checkout.
 */
export const ProductCardBloodVisionSubscription = ({
  className,
  card,
  isFirst = false,
}: ProductCardBloodVisionSubscriptionProps) => {
  const modalClient = useModal();
  const tCart = useTranslations('cart');
  const t = useTranslations('ring');

  // Mirrors ProductCardBloodVision.openLearnMoreModal.
  const openPlanModal = () => {
    void import('components/blood-vision/BloodVisionSubscriptionModal').then(
      ({ default: BloodVisionSubscriptionModal }) => {
        modalClient.set(
          <BloodVisionSubscriptionModal
            card={card}
            onClose={() => modalClient.clear()}
          />,
        );
        modalClient.setFullView();
        modalClient.setCloseBtnHidden(true);
        modalClient.setTop();
        modalClient.setAlignRight();
        modalClient.show();
      },
    );
  };

  return (
    <ShopProductCardRoot
      as="article"
      className={[styles.root, className].filter(Boolean).join(' ')}
      aria-label={card.title}
    >
      <ShopCardPlanTileMedia
        className={styles.bvMedia}
        onClick={openPlanModal}
        aria-label={t('ring.shop.regionPriceFor', { title: card.title })}
      >
        <Image
          src={card.imageSrc}
          alt={card.title}
          height={335}
          width={355}
          priority={isFirst}
          style={{
            objectFit: 'contain',
            width: '100%',
            borderRadius: '12px',
          }}
        />
      </ShopCardPlanTileMedia>
      <ShopCardBody>
        <ShopCardSpecSection className={`specBlock ${styles.specBlock}`}>
          <h3 className="title">{card.title}</h3>
          <p className="description">
            {card.description}{' '}
            <button
              className="learnMoreLink"
              onClick={openPlanModal}
              data-buttontype="check price in your region"
              data-product={card.title}
            >
              {t('ring.shop.regionPrice')}{' '}
              <PlusCircleDark
                fill="#007FF5"
                style={{
                  width: '12px',
                  height: '12px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginLeft: '2px',
                }}
              />
            </button>
          </p>
        </ShopCardSpecSection>
        <ShopCardFooterRow className={`pricing ${styles.pricing}`}>
          <div className="pricing-info">
            <div className="priceBlock">
              <span className="startingAtLabel">
                {tCart('sharedCart.upsell.startsFrom')}
              </span>
              <div className="priceRow">
                <span className="priceValue">
                  ${formatPriceWithComma(card.fromPrice)}
                </span>
                <span className="taxLabel">{card.billingNote}</span>
              </div>
            </div>
          </div>
          <ShopCardPrimaryCta
            className="quickAdd"
            onClick={(e) => {
              e.stopPropagation();
              openPlanModal();
            }}
            data-buttontype="buy now"
            data-product={card.title}
            aria-haspopup="dialog"
          >
            {tCart('sharedCart.upsell.buyNow')}
          </ShopCardPrimaryCta>
        </ShopCardFooterRow>
      </ShopCardBody>
      {card.tag ? <ShopCardTag><ShopCardTagText tag={card.tag} /></ShopCardTag> : null}
    </ShopProductCardRoot>
  );
};
